import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Palette, Save, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * TT-CREATOMATE-DETERMINISTIC-TEMPLATE-CUTOUT-SYSTEM-015
 * Admin-only "Video Şablonu" section: 6 dropdowns (kategori, preset, placement,
 * background, scene set, product media mode) + preset preview description + media
 * resolution status. Only visible for admin/super_admin.
 *
 * Uses server-side registry (/api/admin/creatomate-template-registry) so the frontend
 * never hard-codes enum values or Turkish labels.
 */
const VideoTemplateConfigSection = ({ productId }) => {
  const { user } = useAuth();
  const [registry, setRegistry] = useState(null);
  const [selection, setSelection] = useState({
    videoCategory: '',
    videoTemplatePreset: '',
    productPlacementMode: '',
    videoBackgroundPreset: '',
    videoSceneSet: '',
    productMediaMode: '',
  });
  const [resolved, setResolved] = useState(null);
  const [mediaResolution, setMediaResolution] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgKind, setMsgKind] = useState(''); // 'ok' | 'err'

  const isAdminLike = user && (user.role === 'admin' || user.role === 'super_admin');

  useEffect(() => {
    if (!isAdminLike || !productId) return;
    (async () => {
      try {
        const [regR, cfgR] = await Promise.all([
          axios.get(`${API_URL}/api/admin/creatomate-template-registry`),
          axios.get(`${API_URL}/api/admin/products/${productId}/video-template-config`),
        ]);
        setRegistry(regR.data);
        const stored = cfgR.data?.storedSelection || {};
        setSelection({
          videoCategory: stored.videoCategory || '',
          videoTemplatePreset: stored.videoTemplatePreset || '',
          productPlacementMode: stored.productPlacementMode || '',
          videoBackgroundPreset: stored.videoBackgroundPreset || '',
          videoSceneSet: stored.videoSceneSet || '',
          productMediaMode: stored.productMediaMode || '',
        });
        setResolved(cfgR.data?.resolvedConfig || null);
        setMediaResolution(cfgR.data?.mediaResolution || null);
      } catch {
        /* silent — section stays hidden if endpoints fail */
      }
    })();
  }, [productId, isAdminLike]);

  if (!isAdminLike || !productId || !registry) return null;

  const setField = (k, v) => setSelection((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      // Empty string → send 'reset' so backend clears the field back to category default.
      const payload = {};
      Object.entries(selection).forEach(([k, v]) => {
        payload[k] = v === '' ? 'reset' : v;
      });
      const r = await axios.put(
        `${API_URL}/api/admin/products/${productId}/video-template-config`,
        payload,
      );
      setResolved(r.data?.resolvedConfig || null);
      // Re-fetch to refresh mediaResolution (server recomputes from stored selection)
      try {
        const cfgR = await axios.get(`${API_URL}/api/admin/products/${productId}/video-template-config`);
        setMediaResolution(cfgR.data?.mediaResolution || null);
      } catch { /* silent */ }
      setMsg('Kaydedildi. Yeni video render tetikledikten sonra bu şablon uygulanacak.');
      setMsgKind('ok');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Kaydedilemedi.';
      setMsg(detail);
      setMsgKind('err');
    } finally {
      setBusy(false);
    }
  };

  const resetAll = () => {
    setSelection({
      videoCategory: '',
      videoTemplatePreset: '',
      productPlacementMode: '',
      videoBackgroundPreset: '',
      videoSceneSet: '',
      productMediaMode: '',
    });
  };

  const renderSelect = (fieldKey, options, placeholderLabel, testid) => (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
        {placeholderLabel}
      </label>
      <select
        value={selection[fieldKey]}
        onChange={(e) => setField(fieldKey, e.target.value)}
        className="text-xs font-semibold border-2 border-neutral-300 rounded px-2 py-1.5 bg-white"
        data-testid={testid}
      >
        <option value="">Kategori varsayılanı</option>
        {options.map((o) => (
          <option key={o.id} value={o.id} disabled={o.active === false}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  // Currently-selected preset (fallback → resolved → first apparel preset if any)
  const activePresetId = selection.videoTemplatePreset || resolved?.selectedVideoTemplatePreset;
  const activePresetMeta = (registry.videoTemplatePresets || []).find((p) => p.id === activePresetId);

  return (
    <div
      className="mt-4 pt-4 border-t border-dashed border-emerald-300"
      data-testid="video-template-config-section"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Palette className="w-4 h-4" style={{ color: '#0B6B4F' }} />
        <p className="text-xs font-black uppercase tracking-wider">Video Şablonu (Admin)</p>
        <span className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest bg-emerald-100 text-emerald-800">
          Kategori + Preset + Medya
        </span>
      </div>
      <p className="text-[11px] mb-3" style={{ color: 'var(--text-secondary)' }}>
        Video render&apos;da kullanılacak <strong>kategori, şablon, ürün yerleşimi, arka plan, sahne seti ve görsel kaynağı</strong>nı
        seçin. Boş bırakılan alanlar kategori varsayılanını kullanır. Değişiklik yalnızca <strong>yeni render</strong>&apos;da
        etkindir; mevcut MP4 otomatik olarak yeniden üretilmez.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {renderSelect('videoCategory',         registry.videoCategories,        'Video Kategorisi',   'vtc-category')}
        {renderSelect('videoTemplatePreset',   registry.videoTemplatePresets,   'Şablon Preset',      'vtc-preset')}
        {renderSelect('productPlacementMode',  registry.productPlacementModes,  'Ürün Yerleşimi',     'vtc-placement')}
        {renderSelect('videoBackgroundPreset', registry.videoBackgroundPresets, 'Arka Plan',          'vtc-background')}
        {renderSelect('videoSceneSet',         registry.videoSceneSets,         'Sahne Seti',         'vtc-sceneset')}
        {registry.productMediaModes && renderSelect('productMediaMode', registry.productMediaModes, 'Ürün Görsel Kaynağı', 'vtc-media-mode')}
      </div>

      {/* Selected preset preview description */}
      {activePresetMeta?.previewDescription && (
        <div
          className="mt-3 p-2 rounded border border-emerald-200 bg-emerald-50 flex gap-2"
          data-testid="vtc-preset-preview"
        >
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#0B6B4F' }} />
          <div className="text-[11px]">
            <p className="font-black uppercase tracking-wider text-emerald-900 mb-0.5" style={{ fontSize: 10 }}>
              {activePresetMeta.label} &middot; {activePresetMeta.requiredMediaType || 'any'}
            </p>
            <p style={{ color: '#065F46' }}>{activePresetMeta.previewDescription}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button
          type="button"
          disabled={busy}
          onClick={save}
          className={`text-xs font-black uppercase tracking-wider px-3 py-2 rounded border-2 flex items-center gap-1.5 ${
            busy
              ? 'border-neutral-300 text-neutral-400 cursor-not-allowed'
              : 'border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white'
          }`}
          data-testid="vtc-save-button"
        >
          <Save className="w-3.5 h-3.5" /> {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={resetAll}
          className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border border-neutral-400 hover:bg-neutral-100 flex items-center gap-1.5"
          data-testid="vtc-reset-button"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Kategori Varsayılanına Dön
        </button>
      </div>

      {msg && (
        <p
          className={`mt-2 text-xs font-semibold ${msgKind === 'ok' ? 'text-emerald-800' : 'text-red-700'}`}
          data-testid="vtc-message"
        >
          {msg}
        </p>
      )}

      {resolved && (
        <div
          className="mt-3 p-2 rounded border border-emerald-200 bg-emerald-50"
          data-testid="vtc-resolved-summary"
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-900 mb-1">
            Şu anki çözümlenmiş yapı
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
            {[
              ['Algılanan kategori', resolved.detectedCategory],
              ['Kullanılan kategori', resolved.selectedVideoCategory],
              ['Şablon preset', resolved.selectedVideoTemplatePreset],
              ['Şablon adı', resolved.chosenTemplateName],
              ['Gerekli medya', resolved.chosenTemplateRequiredMediaType],
              ['Ürün yerleşimi', resolved.selectedProductPlacementMode],
              ['Arka plan seçimi', resolved.selectedBackgroundPreset],
              ['Somut arka plan', resolved.concreteBackgroundPreset],
              ['Sahne seti', resolved.selectedSceneSet],
              ['Görsel modu', resolved.selectedProductMediaMode],
              ['Admin override kullanıldı', String(resolved.adminTemplateOverrideUsed)],
              ['Kategori varsayılanı', String(resolved.categoryDefaultApplied)],
              ['Template kaynağı', resolved.templateSource],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 text-[11px]">
                <span className="font-bold text-neutral-700">{k}</span>
                <span className="font-mono text-emerald-900">{v ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v015: Media resolution status + cutout provider warning */}
      {mediaResolution && (
        <div
          className="mt-2 p-2 rounded border border-neutral-300 bg-neutral-50"
          data-testid="vtc-media-resolution"
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-neutral-700 mb-1">
            Şu an render için kullanılacak görsel kaynağı
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
            {[
              ['Görsel kaynağı', mediaResolution.videoImageSource],
              ['Cutout durumu', mediaResolution.cutoutStatus],
              ['Cutout yöntemi', mediaResolution.cutoutMethod],
              ['Cutout sağlayıcı', mediaResolution.cutoutProvider],
              ['Sağlayıcı yapılandırıldı mı', String(mediaResolution.cutoutProviderConfigured)],
              ['İnceleme gerekiyor', String(mediaResolution.cutoutNeedsReview)],
              ['Orijinal görsel kullanıldı', String(mediaResolution.originalImageUsed)],
              ['Manuel temiz görsel kullanıldı', String(mediaResolution.manualCleanAssetUsed)],
              ['İşlenmiş cutout kullanıldı', String(mediaResolution.processedCutoutUsed)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 text-[11px]">
                <span className="font-bold text-neutral-700">{k}</span>
                <span className="font-mono">{v === null || v === undefined || v === '' ? '—' : String(v)}</span>
              </div>
            ))}
          </div>
          {(mediaResolution.cutoutWarnings || []).map((w, i) => (
            <div
              key={i}
              className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-amber-900"
              data-testid={`vtc-cutout-warning-${i}`}
            >
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5 text-amber-700" /> {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoTemplateConfigSection;
